import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API, useBranding } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, CheckCircle, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useTheme } from '@/components/ThemeProvider';
import CaptchaWidget from '@/components/CaptchaWidget';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const { theme } = useTheme();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;

  const planId = searchParams.get('plan');

  const [form, setForm] = useState({ name: '', email: '', password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('');

  // CAPTCHA state
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const captchaRequired = captchaConfig?.captcha_enabled && captchaConfig?.merchant_login_captcha;
  const captchaType = captchaConfig?.captcha_type || 'math';
  const captchaPassed = !captchaRequired || (captchaType === 'math' ? captchaVerified : !!captchaToken);

  useEffect(() => {
    document.title = 'Create Account | CoinPayUp';
    // Fetch captcha config
    axios.get(`${API}/auth/captcha-config`).then(r => setCaptchaConfig(r.data)).catch(() => {});
    if (planId) {
      axios.get(`${API}/plans/public`).then(res => {
        const plan = res.data.find(p => p.id === planId);
        if (plan) setSelectedPlanName(plan.name);
      }).catch(() => {});
    }
  }, [planId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter your full name'); return; }
    if (!form.email.trim()) { toast.error('Please enter your email'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      const refCode = searchParams.get('ref');
      const payload = { full_name: form.name.trim(), email: form.email.trim(), password: form.password };
      if (planId) payload.plan_id = planId;
      if (refCode) payload.ref_code = refCode;
      await register(payload);
      setRegisteredEmail(form.email.trim());
      setSuccess(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registration failed. Please try again.';
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        <div className="w-full max-w-md relative z-10 text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
            <CheckCircle className="h-8 w-8 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Check your email</h2>
            <p className="text-muted-foreground text-sm">
              We've sent a verification link to <span className="text-foreground font-medium">{registeredEmail}</span>.
              Click the link to activate your account and start accepting crypto payments.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Already verified?{' '}
            <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="register-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />

      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        {/* Logo */}
        <div className="flex justify-center mb-8">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-10 object-contain" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-lg">C</span>
            </div>
          )}
        </div>

        <Card className="border-border bg-card" data-testid="register-card">
          <CardHeader>
            <CardTitle className="text-xl">Create your account</CardTitle>
            <CardDescription>Start accepting crypto payments in minutes</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selected plan indicator */}
              {planId && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-primary/10 border border-primary/25">
                  <span className="text-xs text-muted-foreground">Selected plan</span>
                  <span className="text-xs font-semibold text-primary">{selectedPlanName || planId}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reg-name">Full Name</Label>
                <Input
                  id="reg-name"
                  type="text"
                  placeholder="John Smith"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email">Email</Label>
                <Input
                  id="reg-email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password">Password</Label>
                <div className="relative">
                  <Input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required
                    data-testid="register-password-input"
                    className="pr-10"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-confirm">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="reg-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat password"
                    value={form.confirm_password}
                    onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                    required
                    data-testid="register-confirm-input"
                    className="pr-10"
                  />
                  <button type="button" tabIndex={-1} onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                By creating an account you agree to our{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>

              {/* CAPTCHA */}
              {captchaRequired && (
                <CaptchaWidget
                  type={captchaType}
                  siteKey={captchaConfig?.[`${captchaType}_site_key`] || ''}
                  onVerified={v => setCaptchaVerified(v)}
                  onToken={t => setCaptchaToken(t)}
                />
              )}

              <Button
                type="submit"
                className="w-full bg-primary text-white"
                disabled={loading || !captchaPassed}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-5">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="go-to-login-link">Sign in</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
